<template>
  <div class="org-menu-header-side">
    <!-- ER-11292: the name column is sized by flexbox (flex: 1 1 auto; min-width: 0)
         instead of the old calc(100% - 150px) guess, so the title fills the space the
         right-side readiness/menu block actually leaves. The .name-text ellipsis in
         OrgObjectCard only kicks in once the title genuinely exceeds that space. -->
    <div class="org-object-header__name-col">
      <v-row>
        <v-card-text class="col-12 header-status-icon">
          <div class="header-header-container">
            <span class="header-position-custom-icon">
              <Icon v-if="type === 'Position'" name="org_icon_position" />
              <Icon v-else-if="type === 'Role'" name="org_icon_role" />
              <Icon v-else-if="type === 'JobFamily'" name="org_icon_jobfamily" />
              <Icon v-else-if="type === 'JobCategory'" name="org_icon_jobcategory" />
              <slot v-else-if="type === 'Department'" name="icon">
                <DepartmentHeaderIcon />
              </slot>
              <img
                v-else-if="type === 'Responsibility' && lockShow"
                :src="responsibilityIconLocked"
                alt="Locked responsibility"
                class="header-responsibility-icon-img"
              />
              <img
                v-else-if="type === 'Responsibility'"
                :src="responsibilityIconUnlocked"
                alt="Responsibility"
                class="header-responsibility-icon-img"
              />
              <Icon v-else name="org_icon_responsibility" />
            </span>
            <div class="mt-1" style="width: 100%">
              <div class="lock-status">
                <v-img
                  v-if="lockShow && type !== 'Responsibility'"
                  :src="require('@components/assets/header-lock-logo.svg')"
                  :width="20"
                />
                <StatusBadge
                  :status="badgeStatus"
                  :archived="archived"
                  :backgroundColor="badgeStatus === 'archived' ? '#D92D20' : ''"
                />
                <ReadinessChip
                  v-if="showReadinessChip"
                  class="org-object-header__readiness-chip"
                  :readiness="readinessChipData"
                />
              </div>
              <div class="header-object-name" style="width: 100%">
                <slot name="name">
                  <OrgObjectCard
                    :code="code"
                    :name="name"
                    :removeUnderlineToName="true"
                    :dynamicWidth="nameDynamicWidth"
                    :maxWidth="nameMaxWidth"
                  />
                </slot>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-row>
    </div>
    <div class="header-org-menu header-chatter-with-position">
      <RoleAttachmentsBadge
        v-if="type === 'Role'"  
        :activePositions="attachedToPositionsCount"
        :inactivePositions="inactivePositionsCount"
        :activeResponsibilities="attachedToResponsibilitiesCount"
        :inactiveResponsibilities="inactiveResponsibilitiesCount"
      />
      <EmployeeReadinessHeaderBox
        v-if="type === 'Responsibility' && showReadinessBox && !showReadinessChip"
          :readiness="readiness ?? 0"
          :last_month_readiness="last_month_readiness"
          :readinessTitle="'AVG Readiness'"
          :showTooltip="true"
          :tooltipTitle="'The average readiness score across all assignments linked to this responsibility.'"
        />
      <EmployeeReadinessHeaderBox
        v-if="type === 'Department' && showReadinessBox && !showReadinessChip"
        :readiness="readiness ?? 0"
        :last_month_readiness="last_month_readiness"
        readiness-title="AVG Readiness"
      />
      <div
        role="button"
        tabindex="0"
        :aria-label="chatterAriaLabel"
        @click="openChatter(currentPosition)"
        @keydown.enter.prevent="openChatter(currentPosition)"
        @keydown.space.prevent="openChatter(currentPosition)"
        class="hide-left-border center-vertical header-chatter-type-icon"
        :class="{
          'header-chatter-lock-icon': lockShow,
        }"
      >
        <ChatterIcon
          :is_white="false"
          :unread="chatterUnreadCount"
          class="header-chatter-icon"
        />
      </div>
      <!-- <div v-if="lockShow" class="header-lock-icon">
        <v-img
          class="header-chatter-icon"
          :src="require('@components/assets/unlock.svg')"
        />
      </div> -->
      
      <PositionLineMenu
        v-if="type === 'Position'"
        class="header-org-obj-menu-position"
        :position="currentPosition"
        :status="normalizedStatus"
        @action="handlePositionAction"
      />
      <RoleLineMenu
        v-else-if="type === 'Role'"
        class="header-org-obj-menu-role"
        :roleName="name"
        :status="normalizedStatus"
        :showAttachToPositionsBulk="showAttachToPositionsBulk"
        is-header-menu
        @action="handleRoleAction"
      />
      
      <ResponsibilityLineMenu
        v-else-if="type === 'Responsibility' && showMenu"
        class="header-org-obj-menu-responsibility"
        :status="getResponsibilityStatus()"
        :responsibilityName="name"
        is-header-menu
        @action="handleResponsibilityAction"
      />
      <JobFamilyLineMenu
        v-else-if="type === 'JobFamily'"
        class="header-org-obj-menu-jobfamily"
        :family="currentPosition"
        :status="status"
        :position-count="positionCount"
        @action="handleJobFamilyAction"
      />
      <MenuIcon
        v-else-if="type === 'JobCategory'"
        class="header-org-obj-menu-jobcategory"
        :dropdownItems="menuItems"
        @onClick="
          emit('optionClick', { item: $event, data: dynamicModalHeaderState })
        "
      >
        <template #activator>
          <button
            type="button"
            class="header-jobcategory-menu-trigger"
            :aria-label="menuAriaLabel"
          >
            <img
              :src="dotsHorizontalIcon"
              alt=""
              width="24"
              height="24"
              aria-hidden="true"
            />
          </button>
        </template>
      </MenuIcon>
      <MenuIcon
        v-else-if="type === 'Department'"
        class="header-org-obj-menu-department"
        :dropdownItems="menuItems"
        :showTooltip="false"
        @onClick="
          emit('optionClick', { item: $event, data: dynamicModalHeaderState })
        "
      />
      <DropDownMenu
        v-else-if="type !== 'Position' && type !== 'Role' && type !== 'Responsibility'"
        class="header-org-obj-menu"
        :is-open="false"
        :dropdown-items="getOrgObjectDropdownItems(dynamicModalHeaderState)"
        @onClick="
          emit('optionClick', { item: $event, data: dynamicModalHeaderState })
        "
      >
        <template #activator>
          <div class="icon-container">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="icon-svg"
            >
              <path
                d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                stroke="#344054"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                stroke="#344054"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                stroke="#344054"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </template>
      </DropDownMenu>
    </div>
    <ChatterDrawer
      ref="drawerContainer"
      :drawer="chatterDrawerOpen"
      :iconName="drawerHeaderState"
      :type="type"
      :typeValue="name || (currentPosition && currentPosition.name)"
      :listData="listData"
      :object-type="chatterObjectType"
      :object-id="listData?.id ?? currentPosition?.id ?? null"
      @closeDrawer="closeDrawer"
      :chatterList="dataStream"
      :totalCheckpointChatter="totalChatterCount"
      @requestMoreCheckpointsChatter="
        emit('requestMoreCheckpointsChatter', $event)
      "
    />
  </div>
</template>

<script setup>
import { defineProps, ref, computed } from "vue";
import StatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import Icon from "@components/icons/Icon.vue";
import OrgObjectCard from "@components/common/OrgObjectCard.vue";
import DropDownMenu from "@components/DropDownMenu/DropDownMenu.vue";
import MenuIcon from "@components/MenuIcon/MenuIcon.vue";
import DepartmentHeaderIcon from "@components/DepartmentPageHeader/icons/DepartmentHeaderIcon.vue";
import PositionLineMenu from "@components/PositionLineMenu/PositionLineMenu.vue";
import RoleLineMenu from "@components/RoleLineMenu/RoleLineMenu.vue";
import ResponsibilityLineMenu from "@components/ResponsibilityLineMenu/ResponsibilityLineMenu.vue";
import JobFamilyLineMenu from "@components/JobFamilyLineMenu/JobFamilyLineMenu.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import EmployeeReadinessHeaderBox from "@components/EmployeeReadinessHeaderBox/EmployeeReadinessHeaderBox.vue";
import RoleAttachmentsBadge from "@components/RoleAttachmentsBadge/RoleAttachmentsBadge.vue";
import ReadinessChip from "@components/ReadinessChip/ReadinessChip.vue";
import { useReadinessChipFlag } from "@components/utils/useReadinessChipFlag";
const responsibilityIconLocked = require("@components/assets/lock-resp.png");
const responsibilityIconUnlocked = require("@components/assets/responsibility.png");
const dotsHorizontalIcon = require("@components/assets/job-family/dots-horizontal.svg");

const props = defineProps({
  inactivePositionsCount: {
    type: Number,
    default: 0,
  },
  inactiveResponsibilitiesCount: {
    type: Number,
    default: 0,
  },
  attachedToPositionsCount: {
    type: Number,
    default: 0,
  },
  attachedToResponsibilitiesCount: {
    type: Number,
    default: 0,
  },
  /** When type is JobFamily, drives which overflow-menu actions are enabled (e.g. delete). */
  positionCount: {
    type: Number,
    default: 0,
  },
  type: {
    type: String,
    default: "Position",
  },
  status: {
    type: String,
    default: "",
  },
  code: {
    type: String,
    default: "",
  },
  name: {
    type: String,
    default: "",
  },
  archived: {
    type: Boolean,
    default: false,
  },
  lockShow: {
    type: Boolean,
    default: false,
  },
  currentPosition: {
    type: Object,
    default() {
      return {};
    },
  },
  modalHeaderState: {
    type: Object,
  },
  dataStream: {
    type: Array,
    default: () => [],
  },
  totalChatterCount: {
    type: Number,
    default: 0,
  },
  /** null = no readiness data for this object (no chip). */
  readiness: {
    type: Number,
    default: null,
  },
  /** null = last month unknown (the chip omits the change). */
  last_month_readiness: {
    type: Number,
    default: null,
  },
  chatterDrawerOpen: {
    type: Boolean,
    default: false,
  },
  showMenu: {
    type: Boolean,
    default: true,
  },
  showReadinessBox: {
    type: Boolean,
    default: true,
  },
  /** Overflow menu items (name/value pairs; JobCategory items also carry an icon) for the Department and JobCategory headers. */
  menuItems: {
    type: Array,
    default: () => [],
  },
  /** Unread chatter count shown on the header chatter icon. */
  chatterUnreadCount: {
    type: Number,
    default: 0,
  },
  /** When type is Role, show "Attach to Positions in Bulk" in the header menu (e.g. role detail page). */
  showAttachToPositionsBulk: {
    type: Boolean,
    default: false,
  },
  /** Cap the object name to a responsive width and ellipsize instead of stretching the header. */
  nameDynamicWidth: {
    type: Boolean,
    default: false,
  },
  nameMaxWidth: {
    type: String,
    default: "100%",
  },
});

const readinessChipEnabled = useReadinessChipFlag();

const showReadinessChip = computed(
  () =>
    readinessChipEnabled.value &&
    props.showReadinessBox &&
    typeof props.readiness === "number"
);

const readinessChipData = computed(() => ({
  percent: props.readiness,
  changePercent:
    typeof props.last_month_readiness === "number"
      ? props.readiness - props.last_month_readiness
      : null,
}));

const listData = ref({});
const drawerContainer = ref(null);

const dynamicModalHeaderState = computed(() => {
  if (
    props.modalHeaderState &&
    Object.keys(props.modalHeaderState).length > 0
  ) {
    return props.modalHeaderState;
  } else {
    return {
      status: props.status,
      icon: `org_icon_${props.type.toLowerCase()}`,
      label: props.type,
      code: props.code,
      name: props.name,
      archived: props.archived,
    };
  }
});

/** Maps the display `type` prop to the unified feed object type. */
const chatterObjectType = computed(() => {
  switch (props.type) {
    case "Position":
      return "position";
    case "Role":
      return "role";
    case "JobFamily":
      return "job_family";
    case "JobCategory":
      return "job_category";
    default:
      return "responsibility";
  }
});

const drawerHeaderState = computed(() => {
  if (props.type === "Position") {
    return "org_icon_position";
  } else if (props.type === "Role") {
    return "org_icon_role";
  } else if (props.type === "JobFamily") {
    return "org_icon_jobfamily";
  } else if (props.type === "JobCategory") {
    return "org_icon_jobcategory";
  } else {
    return "org_icon_responsibility";
  }
});

const chatterAriaLabel = computed(() =>
  props.name ? `Open chatter for ${props.name}` : "Open chatter"
);

const menuAriaLabel = computed(() =>
  props.name ? `Actions for ${props.name}` : "Actions"
);

const openChatter = (data) => {
  listData.value = data;
  emit("update:chatterDrawerOpen", true);
  emit("reloadChatterList");
  emit("chatterClick");
};

const closeDrawer = () => {
  emit("update:chatterDrawerOpen", false);
};

const emit = defineEmits([
  "optionClick",
  "requestMoreCheckpointsChatter",
  "reloadChatterList",
  "chatterClick",
  "update:chatterDrawerOpen",
]);

const normalizedStatus = computed(() => {
  const s = props.status || '';
  const lower = s.toLowerCase();
  if (lower === 'draft') return 'Draft';
  if (lower === 'active') return 'Active';
  if (lower === 'retired') return 'Retired';
  if (lower === 'archived') return 'Archived';
  return s.charAt(0).toUpperCase() + s.slice(1);
});

const badgeStatus = computed(() => {
  const allowed = new Set([
    "draft",
    "active",
    "retired",
    "inactive",
    "archived",
    "pending",
    "relieved",
    "terminated",
    "detached",
  ]);

  const raw = String(props.status || "")
    .trim()
    .toLowerCase();

  if (props.archived) return "archived";
  if (allowed.has(raw)) return raw;
  return "draft";
});

const handlePositionAction = (actionObj) => {
  const action = actionObj.type;
  const mapping = {
    'activate': 'activate_action',
    'delete': 'delete_action',
    'retire': 'retire_action',
    'reactivate': 'reactivate_action',
    'rename': 'rename_action',
    'clone': 'cloned_action',
    'archive': 'archive_action',
    'unarchive': 'unarchive_action',
    'attachRole': 'attach_position_role'
  };
  const mappedValue = mapping[action] || action;
  emit('optionClick', { item: { value: mappedValue }, data: dynamicModalHeaderState.value });
};

const handleJobFamilyAction = (payload) => {
  if (!payload || !payload.type) return;
  emit('optionClick', { item: { value: payload.type }, data: dynamicModalHeaderState.value });
};

const handleRoleAction = (action) => {
  const mapping = {
    'activate': 'activate_action',
    'delete': 'delete_action',
    'retire': 'retire_action',
    'reactivate': 'reactivate_action',
    'rename': 'rename_action',
    'archive': 'archive_action',
    'unarchive': 'unarchive_action',
    'attachResponsibility': 'attach_responsibility',
    'attachToPositionsBulk': 'attach_role_positions_bulk',
  };
  const mappedValue = mapping[action] || action;
  emit('optionClick', { item: { value: mappedValue }, data: dynamicModalHeaderState.value });
};

// Map status to the format expected by ResponsibilityLineMenu
const getResponsibilityStatus = () => {
  const status = props.status?.toLowerCase() || '';
  const isArchived = props.archived || false;
  
  if (isArchived) return 'Archived';
  if (status === 'draft') return 'Draft';
  if (status === 'active') return 'Active';
  if (status === 'retired') return 'Retired';
  return 'Draft'; // Default fallback
};

// Handle actions from ResponsibilityLineMenu
const handleResponsibilityAction = (actionType) => {
  // Map action types to match the expected format for optionClick
  const actionMap = {
    'activate': 'activate_action',
    'delete': 'delete_action',
    'retire': 'retire_action',
    'reactivate': 'reactivate_action',
    'rename': 'rename_action',
    'archive': 'archive_action',
    'unarchive': 'unarchive_action',
    'attach_to_role': 'attach_role'
  };
  
  const mappedAction = actionMap[actionType] || actionType;
  
  // Emit both the specific responsibility-action event and the generic optionClick for backward compatibility
  emit('optionClick', { item: { value: mappedAction }, data: dynamicModalHeaderState.value });
};

const getOrgObjectDropdownItems = (data) => {
  if (props.type === "Responsibility" || data.label === "Responsibility") {
    const items = [];
    const status = data.status?.toLowerCase();
    const isArchived = data.archived || false;

    // Draft status: Activate, Attach Role, Rename, Delete
    if (status === "draft") {
      items.push({
        name: "Activate",
        value: "activate_action",
        icon: "icons/dropdown/activate.svg",
        order: 1,
      });
      items.push({
        name: "Attach to Role",
        value: "attach_role",
        icon: "icons/dropdown/attach.svg",
        order: 2,
      });
    }

    // Active status: Retire, Attach Role
    if (status === "active") {
      items.push({
        name: "Retire",
        value: "retire_action",
        icon: "icons/dropdown/activate.svg",
        order: 1,
      });
      items.push({
        name: "Attach to Role",
        value: "attach_role",
        icon: "icons/dropdown/attach.svg",
        order: 2,
      });
    }

    // Retired (not archived): Re-activate, Archive
    if (status === "retired" && !isArchived) {
      items.push({
        name: "Re-activate",
        value: "reactivate_action",
        icon: "icons/dropdown/activate.svg",
        order: 1,
      });
      items.push({
        name: "Archive",
        value: "archive_action",
        icon: "icons/dropdown/archive.svg",
        order: 5,
      });
    }

    // Retired (archived): Unarchive only
    if (status === "archived" && isArchived) {
      items.push({
        name: "Unarchive",
        value: "unarchive_action",
        icon: "icons/dropdown/unarchive.svg",
        order: 5,
      });
    }

    // Inactive status: Re-activate
    if (status === "inactive") {
      items.push({
        name: "Re-activate",
        value: "reactivate_action",
        icon: "icons/dropdown/activate.svg",
        order: 1,
      });
    }

    // Rename - Only when not archived
    if (!isArchived) {
      items.push({
        name: "Rename",
        value: "rename_action",
        icon: "icons/dropdown/rename.svg",
        order: 4,
      });
    }

    // Delete - Only when Draft (with negative: true for red color)
    if (status === "draft") {
      items.push({
        name: "Delete",
        value: "delete_action",
        icon: "icons/dropdown/delete.svg",
        negative: true,
        order: 6,
      });
    }

    // Sort by order
    items.sort((a, b) => a.order - b.order);

    return items;
  } else if (props.type === "Role" || data.label === "Role") {
    // Role-specific dropdown items
    let items = [];

    if (!data || !data.hasOwnProperty("status")) {
      return items;
    }

    const isArchived = data.archived || false;

    if (data.status == "draft") {
      items = [
        {
          name: "Activate",
          value: "activate_action",
          icon: "icons/dropdown/activate.svg",
          order: 1,
        },
        {
          name: "Attach Responsibility",
          value: "attach_responsibility",
          icon: "icons/dropdown/attach.svg",
          order: 2,
        },
        {
          name: "Rename",
          value: "rename_action",
          icon: "icons/dropdown/rename.svg",
          order: 3,
        },
        {
          name: "Delete",
          value: "delete_action",
          icon: "icons/dropdown/delete.svg",
          negative: true,
          order: 4,
        },
      ];
    } else if (data.status === "active") {
      items = [
        {
          name: "Retire",
          value: "retire_action",
          icon: "icons/dropdown/activate.svg",
          order: 1,
        },
        {
          name: "Attach Responsibility",
          value: "attach_responsibility",
          icon: "icons/dropdown/attach.svg",
          order: 2,
        },
        {
          name: "Rename",
          value: "rename_action",
          icon: "icons/dropdown/rename.svg",
          order: 3,
        },
      ];
    } else if (data.status == "inactive") {
      items = [
        {
          name: "Re-Activate",
          value: "reactivate_action",
          icon: "icons/dropdown/activate.svg",
          order: 1,
        },
        {
          name: "Rename",
          value: "rename_action",
          icon: "icons/dropdown/rename.svg",
          order: 2,
        },
      ];
    } else if (data.status == "retired") {
      if (isArchived) {
        items = [
          {
            name: "Unarchive",
            value: "unarchive_action",
            icon: "icons/dropdown/unarchive.svg",
            order: 1,
          },
        ];
      } else {
        items = [
          {
            name: "Re-Activate",
            value: "reactivate_action",
            icon: "icons/dropdown/activate.svg",
            order: 1,
          },
          {
            name: "Rename",
            value: "rename_action",
            icon: "icons/dropdown/rename.svg",
            order: 2,
          },
          {
            name: "Archive",
            value: "archive_action",
            icon: "icons/dropdown/archive.svg",
            order: 3,
          },
        ];
      }
    } else if (data.status?.toLowerCase() == "archived" && isArchived) {
      items = [
        {
          name: "Unarchive",
          value: "unarchive_action",
          icon: "icons/dropdown/unarchive.svg",
          order: 1,
        },
      ];
    }
    return items;
  } else {
    // Position-specific dropdown items
    let items = [];

    if (!data || !data.hasOwnProperty("status")) {
      return items;
    }

    if (data.status == "draft") {
      items = [
        {
          name: "Activate",
          value: "activate_action",
          icon: "icons/dropdown/activate.svg",
          order: 1,
        },
        {
          name: "Rename",
          value: "rename_action",
          icon: "icons/dropdown/rename.svg",
          order: 2,
        },
        {
          name: "Delete",
          value: "delete_action",
          icon: "icons/dropdown/delete.svg",
          negative: true,
          order: 3,
        },
        {
          name: "Clone",
          value: "cloned_action",
          icon: "icons/dropdown/clone.png",
          order: 4,
        },
      ];
    } else if (data.status === "active") {
      if (data.assigned_employees && data.assigned_employees.length === 0) {
        items = [
          {
            name: "Retire",
            value: "retire_action",
            icon: "icons/dropdown/activate.svg",
            order: 1,
          },
          // hide for ticket ER-3750
          // {
          //   name: 'De-Activate',
          //   value: 'deactivate_action',
          //   icon: 'icons/dropdown/activate.svg',
          //   order: 2
          // },
          ...items,
        ];
      }

      items = [
        ...items,
        {
          name: "Attach Role",
          value: "attach_position_role",
          icon: "icons/dropdown/attach.svg",
          order: 3,
        },
        {
          name: "Rename",
          value: "rename_action",
          icon: "icons/dropdown/rename.svg",
          order: 4,
        },
        {
          name: "Clone",
          value: "cloned_action",
          icon: "icons/dropdown/clone.png",
          order: 5,
        },
      ];
    } else if (data.status == "inactive") {
      items = [
        {
          name: "Re-Activate",
          value: "reactivate_action",
          icon: "icons/dropdown/activate.svg",
          order: 1,
        },
        {
          name: "Rename",
          value: "rename_action",
          icon: "icons/dropdown/rename.svg",
          order: 2,
        },
        {
          name: "Clone",
          value: "cloned_action",
          icon: "icons/dropdown/clone.png",
          order: 3,
        },
      ];
    } else if (data.status == "retired") {
      if (data.archived === true) {
        items = [
          {
            name: "Unarchive",
            value: "unarchive_action",
            icon: "icons/dropdown/unarchive.svg",
            order: 1,
          },
        ];
      } else {
        items = [
          {
            name: "Re-Activate",
            value: "reactivate_action",
            icon: "icons/dropdown/activate.svg",
            order: 1,
          },
          {
            name: "Rename",
            value: "rename_action",
            icon: "icons/dropdown/rename.svg",
            order: 2,
          },
          {
            name: "Clone",
            value: "cloned_action",
            icon: "icons/dropdown/clone.png",
            order: 3,
          },
          {
            name: "Archive",
            value: "archive_action",
            icon: "icons/dropdown/archive.svg",
            order: 4,
          },
        ];
      }
    } else if (data.status?.toLowerCase() == "archived" || data.archived === true) {
      items = [
        {
          name: "Unarchive",
          value: "unarchive_action",
          icon: "icons/dropdown/unarchive.svg",
          order: 1,
        },
      ];
    }
    return items;
  }
};
</script>
<style>
/* ER-11292: real flexbox sizing for the header row. The name/status column grows to
   fill whatever space the right-side readiness/menu block leaves (flex: 1 1 auto)
   and can shrink below its content (min-width: 0) so the OrgObjectCard .name-text
   ellipsis kicks in only when the title genuinely exceeds that space. The old
   calc(100% - 150px) guess truncated the title well before it needed to. */
.org-object-header__name-col {
  flex: 1 1 auto;
  min-width: 0;
}
.header-org-menu {
  position: relative;
  right: 25px;
  top: 10px;
}
.header-chatter-with-position {
  display: flex;
  align-items: center;
  gap: 15px;
  flex: 0 0 auto;
}
/* ER-12583: the chatter control is a 24px-tall box centered in the same flex row as the
   overflow menu; neither carries a vertical offset, so they always share one line. */
.header-chatter-type-icon {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-sizing: border-box;
  height: 24px;
  padding: 0 6px;
  position: relative;
  left: 12px;
}
.header-chatter-type-icon:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
  border-radius: 4px;
}
.org-menu-header-side {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.header-status-icon {
  margin-left: 20px;
  margin-top: 10px;
}
.header-header-container {
  display: flex;
  column-gap: 10px;
}
.header-object-name .url-name {
  text-decoration: none;
}
.header-object-name .url-name:hover {
  background-color: transparent;
}
.header-position-custom-icon svg,
.header-position-custom-icon .header-responsibility-icon-img {
  width: 50px;
  height: 50px;
}
.header-position-custom-icon .header-responsibility-icon-img {
  object-fit: contain;
}
.header-position-custom-icon .job-family-icon.job-family-icon--normal,
.header-position-custom-icon .job-category-icon.job-category-icon--normal {
  width: 50px;
  height: 50px;
}
.header-object-name .org-object-card {
  flex-direction: row-reverse;
  gap: 10px;
  display: inline-flex !important;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
}
/* ER-11292 (generalizing the ER-10088 chain): let every flex level between the
   name column and .name-text shrink below content so ellipsis is driven by the
   space the right-side block actually leaves, for every entity type this header
   renders (Positions, Roles, Responsibilities, Departments, Employees). */
.org-menu-header-side .header-header-container > .mt-1 {
  min-width: 0;
}
.org-menu-header-side .header-object-name .object-card-name {
  min-width: 0;
}
.header-object-name .org-object-card:hover {
  background: none;
}
.header-object-name {
  margin-top: -8px;
  margin-left: 1px;
}
.header-object-name .org-object-card .el-tooltip._parent-content {
  width: auto !important;
}
.header-org-obj-menu {
  position: relative;
}
/* For Position, Job Family, Job Category and Department (horizontal offset only, ER-12583) */
.header-org-obj-menu-position,
.header-org-obj-menu-jobfamily,
.header-org-obj-menu-jobcategory,
.header-org-obj-menu-department {
  position: relative;
  right: 8px;
}
/* For Job Category */
.header-jobcategory-menu-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
}
.header-jobcategory-menu-trigger:hover {
  background: #f2f4f7;
}
.header-jobcategory-menu-trigger:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}
/* For Role */
.header-org-obj-menu-role {
  position: relative;
  right: 8px;
}
/* For Responsibility */
.header-org-obj-menu-responsibility {
  position: relative;
  right: 5px;
}
.header-org-obj-menu .ex-generic-selector-measurement-clone {
  position: fixed !important;
}
.header-object-name .code-text {
  color: #98a2b3;
}
.lock-status {
  display: inline-flex;
  gap: 5px;
}
.org-object-header__readiness-chip {
  margin-left: 3px;
}
.header-lock-icon {
  position: relative;
  top: 5px;
  left: 5px;
}
.header-chatter-lock-icon {
  left: 6px !important;
}
.header-org-menu .ex-employee-readiness-box {
  margin-right: 0px !important;
}
/* ER-10088: drop the fixed 120px cap on the readiness progress bar so the header
   menu can shrink with the name truncation flex chain; the bar already sizes from
   its own component styles and the old max-width fought the responsive name cap. */
</style>
